import React from 'react';
import { get, has } from 'lodash';
import { useTranslation } from 'react-i18next';
import { AgeValue, BreastPathology, OptionKeyValue } from '@myriadgenetics/mgh-types';

import './index.scss';

export enum YesNo {
  Yes = 'yes',
  No = 'no',
}

const SEPARATOR = ':';

const yesNoMapper = (val: boolean, noKey?: string) => (val ? YesNo.Yes : noKey || YesNo.No);
interface Props {
  pathology: BreastPathology;
}

function BreastCancerDetails({ pathology }: Props) {
  const isLeftBreast = ({ isBilateral, affected }: BreastPathology): boolean =>
    Boolean(!isBilateral && affected && affected[0].optionValue === 'pathology.breast.left');
  const isRightBreast = ({ isBilateral, affected }: BreastPathology): boolean =>
    Boolean(!isBilateral && affected && affected[0].optionValue === 'pathology.breast.right');
  const isSelfBilateral = ({ affected }: BreastPathology): boolean => Boolean(affected && affected[0].optionValue === 'pathology.breast.bilateral');
  const isRelativeBilateral = ({ isBilateral, breast }: BreastPathology): boolean => Boolean(isBilateral && !breast);
  const isDcis = ({ type, breast }: BreastPathology): boolean => Boolean(type === 'cancerHistory.cancers.dcis' && breast);
  const isDcisBilateral = ({ type, breast }: BreastPathology): boolean =>
    isDcis(pathology) && breast?.affected?.[0]?.optionValue === 'pathology.breast.bilateral';

  const isAgeRange = (age: AgeValue | undefined) => Array.isArray(age);

  const hasMetastaticOrBeingTreated = (pathology: BreastPathology, valueKey: 'metastatic' | 'currentTreatment') => {
    if (!has(pathology, valueKey) && !has(pathology, `left.${valueKey}`) && !has(pathology, `right.${valueKey}`) && !has(pathology, `breast.${valueKey}`)) {
      return undefined;
    }
    return true;
  };

  const getSideToDisplay = (pathology: BreastPathology) => {
    if (isLeftBreast(pathology) || isRightBreast(pathology)) {
      return (
        <React.Fragment>
          <BreastCancerDetail
            className="breast-cancer-details__details"
            bilateral={false}
            side={isLeftBreast(pathology) ? 'review.left' : 'review.right'}
            age={get(pathology, 'age', 0)}
            type={get(pathology, 'breast.details[0].optionValue', 'pathology.breast.none')}
            tripleNegative={yesNoMapper(get(pathology, 'breast.tripleNegative', false), 'pathology.noOrUnknown')}
            metastatic={
              hasMetastaticOrBeingTreated(pathology, 'metastatic') && yesNoMapper(get(pathology, 'breast.metastatic', false), 'pathology.noOrUnknown')
            }
            currentlyBeingTreated={hasMetastaticOrBeingTreated(pathology, 'currentTreatment') && yesNoMapper(get(pathology, 'breast.currentTreatment', false))}
          />
        </React.Fragment>
      );
    }
    return (
      <React.Fragment>
        <BreastCancerDetail
          className="breast-cancer-details__details"
          bilateral={false}
          age={isAgeRange(get(pathology, 'age')) ? get(pathology, 'age[0].optionValue', 0) : get(pathology, 'age', 0)}
          type={get(pathology, 'breast.details[0].optionValue', 'pathology.breast.none')}
          tripleNegative={yesNoMapper(get(pathology, 'breast.tripleNegative', false), 'pathology.noOrUnknown')}
          metastatic={hasMetastaticOrBeingTreated(pathology, 'metastatic') && yesNoMapper(get(pathology, 'breast.metastatic', false), 'pathology.noOrUnknown')}
          currentlyBeingTreated={hasMetastaticOrBeingTreated(pathology, 'currentTreatment') && yesNoMapper(get(pathology, 'breast.currentTreatment', false))}
        />
      </React.Fragment>
    );
  };

  const dcisPathologyToDisplay = (pathology: BreastPathology) => {
    if (isDcisBilateral(pathology)) {
      return (
        <React.Fragment>
          <BreastCancerDetail
            className="breast-cancer-details__details"
            bilateral
            side="review.left"
            age={get(pathology, 'leftBreastAge', 0)}
            tripleNegative={yesNoMapper(get(pathology, 'left.tripleNegative', false), 'pathology.noOrUnknown')}
            metastatic={hasMetastaticOrBeingTreated(pathology, 'metastatic') && yesNoMapper(get(pathology, 'left.metastatic', false), 'pathology.noOrUnknown')}
            currentlyBeingTreated={hasMetastaticOrBeingTreated(pathology, 'currentTreatment') && yesNoMapper(get(pathology, 'left.currentTreatment', false))}
          />
          <BreastCancerDetail
            className="breast-cancer-details__details"
            side="review.right"
            age={get(pathology, 'rightBreastAge', 0)}
            tripleNegative={yesNoMapper(get(pathology, 'right.tripleNegative', false), 'pathology.noOrUnknown')}
            metastatic={hasMetastaticOrBeingTreated(pathology, 'metastatic') && yesNoMapper(get(pathology, 'right.metastatic', false), 'pathology.noOrUnknown')}
            currentlyBeingTreated={hasMetastaticOrBeingTreated(pathology, 'currentTreatment') && yesNoMapper(get(pathology, 'right.currentTreatment', false))}
          />
        </React.Fragment>
      );
    }
    return (
      <BreastCancerDetail
        className="breast-cancer-details__dcis-details"
        bilateral={false}
        age={isAgeRange(get(pathology, 'age')) ? get(pathology, 'age[0].optionValue', 0) : get(pathology, 'age', 0)}
        tripleNegative={yesNoMapper(get(pathology, 'breast.tripleNegative', false), 'pathology.noOrUnknown')}
        metastatic={hasMetastaticOrBeingTreated(pathology, 'metastatic') && yesNoMapper(get(pathology, 'breast.metastatic', false), 'pathology.noOrUnknown')}
        currentlyBeingTreated={hasMetastaticOrBeingTreated(pathology, 'currentTreatment') && yesNoMapper(get(pathology, 'breast.currentTreatment', false))}
      />
    );
  };

  const breastPathologyToDisplay = (pathology: BreastPathology) => {
    if (isDcis(pathology)) {
      return dcisPathologyToDisplay(pathology);
    }
    if (isSelfBilateral(pathology)) {
      return (
        <React.Fragment>
          <BreastCancerDetail
            className="breast-cancer-details__details"
            bilateral
            side="review.left"
            age={get(pathology, 'leftBreastAge', 0)}
            type={get(pathology, 'left.details[0].optionValue', 'pathology.breast.none')}
            tripleNegative={yesNoMapper(get(pathology, 'left.tripleNegative', false), 'pathology.noOrUnknown')}
            metastatic={hasMetastaticOrBeingTreated(pathology, 'metastatic') && yesNoMapper(get(pathology, 'left.metastatic', false), 'pathology.noOrUnknown')}
            currentlyBeingTreated={hasMetastaticOrBeingTreated(pathology, 'currentTreatment') && yesNoMapper(get(pathology, 'left.currentTreatment', false))}
          />
          <BreastCancerDetail
            className="breast-cancer-details__details"
            side="review.right"
            age={get(pathology, 'rightBreastAge', 0)}
            type={get(pathology, 'right.details[0].optionValue', 'pathology.breast.none')}
            tripleNegative={yesNoMapper(get(pathology, 'right.tripleNegative', false), 'pathology.noOrUnknown')}
            metastatic={hasMetastaticOrBeingTreated(pathology, 'metastatic') && yesNoMapper(get(pathology, 'right.metastatic', false), 'pathology.noOrUnknown')}
            currentlyBeingTreated={hasMetastaticOrBeingTreated(pathology, 'currentTreatment') && yesNoMapper(get(pathology, 'right.currentTreatment', false))}
          />
        </React.Fragment>
      );
    }
    if (isRelativeBilateral(pathology)) {
      return (
        <React.Fragment>
          <BreastCancerDetail
            className="breast-cancer-details__bilateral"
            bilateral
            side="review.first"
            age={isAgeRange(get(pathology, 'first.age')) ? get(pathology, 'first.age[0].optionValue', 0) : get(pathology, 'first.age', 0)}
            type={get(pathology, 'first.details[0].optionValue', 'pathology.breast.none')}
            tripleNegative={yesNoMapper(get(pathology, 'first.tripleNegative', false), 'pathology.noOrUnknown')}
          />
          <BreastCancerDetail
            className="breast-cancer-details__bilateral"
            side="review.second"
            age={isAgeRange(get(pathology, 'second.age')) ? get(pathology, 'second.age[0].optionValue', 0) : get(pathology, 'second.age', 0)}
            type={get(pathology, 'second.details[0].optionValue', 'pathology.breast.none')}
            tripleNegative={yesNoMapper(get(pathology, 'second.tripleNegative', false), 'pathology.noOrUnknown')}
          />
        </React.Fragment>
      );
    }
    if (!isSelfBilateral(pathology) && !isRelativeBilateral(pathology)) {
      return getSideToDisplay(pathology);
    }
  };

  return <div className="breast-cancer-details">{breastPathologyToDisplay(pathology)}</div>;
}

type DetailProps = {
  className?: string;
  side?: string;
  age: string | number | OptionKeyValue[];
  type?: string;
  tripleNegative: string;
  currentlyBeingTreated?: string | undefined;
  metastatic?: string | undefined;
  bilateral?: boolean;
};

function BreastCancerDetail({ className, side, age, type, tripleNegative, currentlyBeingTreated, metastatic, bilateral }: DetailProps) {
  const { t } = useTranslation();

  return (
    <ul className={`breast-cancer-details__detail ${className || ''}`}>
      {bilateral !== undefined && (
        <li className="breast-cancer-details__detail-bilateral text-medium--regular">
          {t('review.bilateral')}
          {SEPARATOR} <span className="text-medium--semi-bold">{bilateral ? t('yes') : t('no')}</span>
        </li>
      )}
      {side && <li className="breast-cancer-details__detail-side text-medium--regular">{t(side)}</li>}
      <ul className="breast-cancer-details__detail-list">
        <li className="text-medium--regular">
          {t('review.ageOfDiagnosis')}
          {SEPARATOR} <span className="text-medium--semi-bold">{t(String(age))}</span>
        </li>
        {type && (
          <li className="text-medium--regular">
            {t('review.details')}
            {SEPARATOR} <span className="text-medium--semi-bold">{t(type)}</span>
          </li>
        )}
        <li className="text-medium--regular">
          {t('review.tripleNegative')}
          {SEPARATOR} <span className="text-medium--semi-bold">{t(tripleNegative)}</span>
        </li>
        {metastatic && metastatic !== undefined && (
          <li className="text-medium--regular">
            {t('review.metastatic')}
            {SEPARATOR} <span className="text-medium--semi-bold">{t(metastatic)}</span>
          </li>
        )}
        {currentlyBeingTreated && currentlyBeingTreated !== undefined && (
          <li className="text-medium--regular">
            {t('review.currentlyBeingTreated')}
            {SEPARATOR} <span className="text-medium--semi-bold">{t(currentlyBeingTreated)}</span>
          </li>
        )}
      </ul>
    </ul>
  );
}

export default BreastCancerDetails;
